import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import AdminLayout from '../layout/AdminLayout';
import BotSelector from '../layout/BotSelector';
import {
  listBots,
  getFeatures,
  getAnalyticsOverview,
  getAnalyticsDailyMessages,
  getAnalyticsTopUsers,
  getAnalyticsBroadcasts,
  getAnalyticsUserMessages,
  AnalyticsUnavailableError,
  AnalyticsForbiddenError,
  AnalyticsDisabledError,
  AnalyticsFilters,
  AnalyticsOverview,
  AnalyticsChatFeature,
  DailyMessage,
  TopUser,
  BroadcastStat,
  UserMessage,
} from '../../services/adminApiService';
import { Bot } from '../../services/adminApiService';
import {
  getReviewPermission,
  getAllBots,
  getProviderBotAnalytics,
  ReviewBot,
  ProviderBotOverview,
  ProviderBotUserMessage,
} from '../../services/providerReviewService';
import AnalyticsChat from './AnalyticsChat';
import { renderMarkdown } from './markdown';
import './AnalyticsPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = 'analyticsSelectedBotId';
const SESSION_SOURCE_KEY = 'analyticsSource';
const SESSION_PROVIDER_BOT_KEY = 'analyticsSelectedProviderBotId';

/**
 * Two bot planes share this tab:
 *  - 'provider': any Agent-Provider marketplace bot (reviewEnabled admins) —
 *    shows exactly the owner's 分析 view (provider API, read-only DB user).
 *  - 'curated': the LINE/curated bots this admin was granted in
 *    bot-access-permissions (admin API, adds LINE/webchat split + broadcasts).
 */
type Source = 'provider' | 'curated';

interface AppliedScope {
  source: Source;
  botId: string;
  from?: string;
  to?: string;
}

interface ProviderGroup {
  providerId: string;
  label: string;
  bots: ReviewBot[];
}

const STATUS_ORDER = ['Published', 'Testing', 'ReadyForTesting', 'InReview', 'Submitted', 'Suspended', 'Draft'];

function providerLabel(b: ReviewBot): string {
  if (b.providerName && b.providerEmail) return `${b.providerName} <${b.providerEmail}>`;
  return b.providerName || b.providerEmail || b.providerId;
}

/** Group the cross-provider bot list by provider, providers and bots sorted. */
export function groupBotsByProvider(bots: ReviewBot[]): ProviderGroup[] {
  const byId = new Map<string, ProviderGroup>();
  for (const b of bots) {
    if (!b.providerId) continue;
    let g = byId.get(b.providerId);
    if (!g) {
      g = { providerId: b.providerId, label: providerLabel(b), bots: [] };
      byId.set(b.providerId, g);
    }
    g.bots.push(b);
  }
  const rank = (s: string) => {
    const i = STATUS_ORDER.indexOf(s);
    return i === -1 ? STATUS_ORDER.length : i;
  };
  const groups: ProviderGroup[] = Array.from(byId.values());
  groups.forEach((g) =>
    g.bots.sort((a, b) => rank(a.status) - rank(b.status) || (a.displayName || '').localeCompare(b.displayName || ''))
  );
  return groups.sort((a, b) => a.label.localeCompare(b.label));
}

/** Date inputs are inclusive; the API's `to` is exclusive — send the next day. */
function toRange(from: string, to: string): { from?: string; to?: string } {
  let toExclusive: string | undefined;
  if (to) {
    const d = new Date(`${to}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    toExclusive = toIsoDate(d);
  }
  return { from: from || undefined, to: toExclusive };
}

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // per-tab convenience only
  }
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29); // last 30 days inclusive
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatNumber(n: number | undefined | null): string {
  if (n == null) return '-';
  return n.toLocaleString();
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: number | undefined;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value }) => (
  <div className="kpi-card">
    <span className="kpi-label">{label}</span>
    <span className="kpi-value">{value != null ? value.toLocaleString() : '-'}</span>
  </div>
);

// ── UserMessagesPanel ─────────────────────────────────────────────────────────

type PanelMessage = Pick<UserMessage, 'message_timestamp' | 'content'> & {
  role: UserMessage['role'] | ProviderBotUserMessage['role'];
  source: string;
};

interface UserMessagesPanelProps {
  userId: string;
  messages: PanelMessage[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

const UserMessagesPanel: React.FC<UserMessagesPanelProps> = ({
  userId,
  messages,
  loading,
  error,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <div className="user-messages-overlay" role="dialog" aria-modal="true">
      <div className="user-messages-panel">
        <div className="user-messages-header">
          <div className="user-messages-title-group">
            <h3 className="user-messages-title">{t('analytics.userMessages.title')}</h3>
            <span className="user-messages-userid" title={userId}>{userId}</span>
          </div>
          <button
            className="user-messages-close"
            onClick={onClose}
            aria-label={t('analytics.userMessages.close')}
          >
            &#x2715;
          </button>
        </div>

        <div className="user-messages-body">
          {loading && (
            <div className="user-messages-state">
              <p>{t('analytics.userMessages.loading')}</p>
            </div>
          )}

          {!loading && error && (
            <div className="user-messages-state user-messages-error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && messages.length === 0 && (
            <div className="user-messages-state">
              <p>{t('analytics.userMessages.noData')}</p>
            </div>
          )}

          {!loading && !error && messages.length > 0 && (
            <ul className="user-messages-list">
              {messages.map((msg, idx) => (
                <li key={idx} className={`user-message-item user-message-item--${msg.role}`}>
                  <div className="user-message-meta">
                    <span className={`role-badge role-badge--${msg.role}`}>
                      {t(`analytics.userMessages.role.${msg.role}`)}
                    </span>
                    <span className="user-message-source">{msg.source}</span>
                    <span className="user-message-timestamp">{formatDateTime(msg.message_timestamp)}</span>
                  </div>
                  {/* Bot/provider replies are Markdown (same renderer as the
                      analytics chat — React elements only, no raw HTML);
                      customer text stays verbatim. */}
                  {msg.role === 'assistant' || msg.role === 'provider' ? (
                    <div className="user-message-content user-message-content--md analytics-chat-md">
                      {renderMarkdown(msg.content || '')}
                    </div>
                  ) : (
                    <div className="user-message-content">{msg.content}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ── AnalyticsPage ─────────────────────────────────────────────────────────────

const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();

  // Curated (LINE) bots granted to this admin
  const [bots, setBots] = useState<Bot[]>([]);
  const [botsLoading, setBotsLoading] = useState(true);
  const [botsError, setBotsError] = useState<string | null>(null);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);

  // Provider (marketplace) bots — every provider's bots, reviewEnabled admins only
  const [canReview, setCanReview] = useState(false);
  const [providerBots, setProviderBots] = useState<ReviewBot[]>([]);
  const [providerBotsLoading, setProviderBotsLoading] = useState(true);
  const [providerBotsError, setProviderBotsError] = useState<string | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [selectedProviderBotId, setSelectedProviderBotId] = useState<string | null>(null);

  const [source, setSource] = useState<Source | null>(null);

  // Filters (working copy edited by user)
  const defaults = defaultDateRange();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);

  // What the last fetch used
  const [applied, setApplied] = useState<AppliedScope | null>(null);

  // Data
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [providerOverview, setProviderOverview] = useState<ProviderBotOverview | null>(null);
  const [daily, setDaily] = useState<DailyMessage[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [broadcasts, setBroadcasts] = useState<BroadcastStat[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [analyticsDisabled, setAnalyticsDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // User messages drill-down state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userMessages, setUserMessages] = useState<PanelMessage[]>([]);
  const [userMessagesLoading, setUserMessagesLoading] = useState(false);
  const [userMessagesError, setUserMessagesError] = useState<string | null>(null);

  // Analytics chat feature flag
  const [analyticsChat, setAnalyticsChat] = useState<AnalyticsChatFeature>({ enabled: false });

  const providerGroups = useMemo(() => groupBotsByProvider(providerBots), [providerBots]);
  const selectedGroup = providerGroups.find((g) => g.providerId === selectedProviderId) || null;
  const selectedProviderBot = providerBots.find((b) => b.botId === selectedProviderBotId) || null;

  // Load both bot lists + feature flags once on mount
  useEffect(() => {
    const fetchCurated = async (): Promise<Bot[]> => {
      try {
        const res = await listBots();
        setBots(res.bots);
        if (res.bots.length > 0) {
          const saved = readSession(SESSION_STORAGE_KEY);
          setSelectedBotId(res.bots.find((b) => b.botId === saved) ? saved : res.bots[0].botId);
        }
        return res.bots;
      } catch {
        setBotsError(t('analytics.errorLoadingBots'));
        return [];
      } finally {
        setBotsLoading(false);
      }
    };

    const fetchProvider = async (): Promise<ReviewBot[]> => {
      try {
        const ok = await getReviewPermission();
        setCanReview(ok);
        if (!ok) return [];
        const all = await getAllBots('all');
        setProviderBots(all);
        const groups = groupBotsByProvider(all);
        if (groups.length > 0) {
          const saved = readSession(SESSION_PROVIDER_BOT_KEY);
          const savedBot = all.find((b) => b.botId === saved && b.providerId);
          const bot = savedBot || groups[0].bots[0];
          setSelectedProviderId(bot.providerId);
          setSelectedProviderBotId(bot.botId);
        }
        return all;
      } catch {
        setProviderBotsError(t('analytics.provider.errorLoadingBots'));
        return [];
      } finally {
        setProviderBotsLoading(false);
      }
    };

    const fetchFeaturesForChat = async () => {
      try {
        const f = await getFeatures();
        setAnalyticsChat(f.analyticsChat);
      } catch {
        // fail closed — chat panel simply stays hidden
      }
    };

    Promise.all([fetchCurated(), fetchProvider()]).then(([curated, provider]) => {
      const hasProvider = provider.some((b) => b.providerId);
      const saved = readSession(SESSION_SOURCE_KEY) as Source | null;
      if (saved === 'curated' && curated.length > 0) setSource('curated');
      else if (saved === 'provider' && hasProvider) setSource('provider');
      else if (hasProvider) setSource('provider');
      else if (curated.length > 0) setSource('curated');
    });
    fetchFeaturesForChat();
  }, [t]);

  const fetchAll = useCallback(async (scope: AppliedScope) => {
    setLoading(true);
    setError(null);
    setUnavailable(false);
    setForbidden(false);
    setAnalyticsDisabled(false);
    try {
      if (scope.source === 'provider') {
        const range = { from: scope.from, to: scope.to };
        const [ov, dl, tu] = await Promise.all([
          getProviderBotAnalytics<ProviderBotOverview>(scope.botId, 'overview', range),
          getProviderBotAnalytics<DailyMessage[]>(scope.botId, 'messages-daily', range),
          getProviderBotAnalytics<TopUser[]>(scope.botId, 'top-users', range),
        ]);
        setProviderOverview(ov);
        setOverview(null);
        setDaily(dl);
        setTopUsers(tu);
        setBroadcasts([]);
      } else {
        const filters: AnalyticsFilters = { botId: scope.botId, from: scope.from, to: scope.to };
        const [ov, dl, tu, bc] = await Promise.all([
          getAnalyticsOverview(filters),
          getAnalyticsDailyMessages(filters),
          getAnalyticsTopUsers(filters),
          getAnalyticsBroadcasts(filters),
        ]);
        setOverview(ov);
        setProviderOverview(null);
        setDaily(dl);
        setTopUsers(tu);
        setBroadcasts(bc);
      }
    } catch (err) {
      if (err instanceof AnalyticsDisabledError) {
        setAnalyticsDisabled(true);
      } else if (err instanceof AnalyticsUnavailableError) {
        setUnavailable(true);
      } else if (err instanceof AnalyticsForbiddenError) {
        setForbidden(true);
      } else {
        setError(t('analytics.errorLoading'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  const activeBotId = source === 'provider' ? selectedProviderBotId : source === 'curated' ? selectedBotId : null;

  const runFetch = (src: Source | null, botId: string | null) => {
    if (!src || !botId) return;
    const scope: AppliedScope = { source: src, botId, ...toRange(fromDate, toDate) };
    setApplied(scope);
    fetchAll(scope);
  };

  // Fetch whenever the active source/bot resolves or changes
  useEffect(() => {
    runFetch(source, activeBotId);
    // Date edits only apply via the Apply button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, activeBotId]);

  const handleSourceSelect = (next: Source) => {
    if (next === source) return;
    writeSession(SESSION_SOURCE_KEY, next);
    setSource(next);
  };

  const handleBotSelect = (newBotId: string) => {
    writeSession(SESSION_STORAGE_KEY, newBotId);
    setSelectedBotId(newBotId);
  };

  const handleProviderSelect = (providerId: string) => {
    const group = providerGroups.find((g) => g.providerId === providerId);
    setSelectedProviderId(providerId);
    const first = group?.bots[0];
    if (first) {
      writeSession(SESSION_PROVIDER_BOT_KEY, first.botId);
      setSelectedProviderBotId(first.botId);
    }
  };

  const handleProviderBotSelect = (botId: string) => {
    writeSession(SESSION_PROVIDER_BOT_KEY, botId);
    setSelectedProviderBotId(botId);
  };

  const handleApply = () => runFetch(source, activeBotId);

  const handleUserRowClick = useCallback(async (userId: string) => {
    if (!applied) return;
    setSelectedUserId(userId);
    setUserMessages([]);
    setUserMessagesError(null);
    setUserMessagesLoading(true);
    try {
      const msgs: PanelMessage[] = applied.source === 'provider'
        ? await getProviderBotAnalytics<ProviderBotUserMessage[]>(applied.botId, 'user-messages', {
            from: applied.from,
            to: applied.to,
            userId,
          })
        : await getAnalyticsUserMessages({
            botId: applied.botId,
            from: applied.from,
            to: applied.to,
            userId,
          });
      setUserMessages(msgs);
    } catch (err) {
      setUserMessagesError(t('analytics.userMessages.errorLoading'));
    } finally {
      setUserMessagesLoading(false);
    }
  }, [applied, t]);

  const handleCloseUserMessages = () => {
    setSelectedUserId(null);
    setUserMessages([]);
    setUserMessagesError(null);
  };

  const listsLoading = botsLoading || providerBotsLoading;
  const hasProviderBots = canReview && providerGroups.length > 0;
  const hasCuratedBots = bots.length > 0;
  const listsError = !hasProviderBots && !hasCuratedBots ? botsError || providerBotsError : null;
  const showSourceTabs = hasProviderBots && hasCuratedBots;
  const appliedSource = applied?.source;

  return (
    <AdminLayout>
      <div className="analytics-page">
        {/* Page header */}
        <div className="page-header">
          <h2 className="page-title">{t('analytics.title')}</h2>
          <p className="page-description">{t('analytics.description')}</p>
        </div>

        {/* Feature flag disabled — shown regardless of bot state */}
        {analyticsDisabled && (
          <div className="analytics-disabled">
            <p>{t('analytics.featureDisabled')}</p>
          </div>
        )}

        {/* Bot lists loading */}
        {!analyticsDisabled && listsLoading && (
          <div className="analytics-loading">
            <p>{t('analytics.loadingBots')}</p>
          </div>
        )}

        {/* Bot lists error (only when nothing at all could be loaded) */}
        {!analyticsDisabled && !listsLoading && listsError && (
          <div className="analytics-error">
            <p>{listsError}</p>
          </div>
        )}

        {/* No bots available in either plane */}
        {!analyticsDisabled && !listsLoading && !listsError && !hasProviderBots && !hasCuratedBots && (
          <div className="analytics-error">
            <p>{t('analytics.noBots')}</p>
          </div>
        )}

        {/* Main content */}
        {!analyticsDisabled && !listsLoading && source && (
          <>
            {/* Source switch — provider marketplace bots vs curated LINE bots */}
            {showSourceTabs && (
              <div className="analytics-source-tabs" role="tablist" aria-label={t('analytics.source.label')}>
                {(['provider', 'curated'] as Source[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="tab"
                    aria-selected={source === s}
                    className={`analytics-source-tab${source === s ? ' analytics-source-tab--active' : ''}`}
                    onClick={() => handleSourceSelect(s)}
                  >
                    {t(`analytics.source.${s}`)}
                  </button>
                ))}
              </div>
            )}

            {/* Bot pickers + date filter */}
            <div className="analytics-filters">
              {source === 'provider' && (
                <>
                  <div className="filter-group filter-group--wide">
                    <label className="filter-label" htmlFor="analytics-provider-select">
                      {t('analytics.provider.provider')}
                    </label>
                    <select
                      id="analytics-provider-select"
                      className="filter-select"
                      value={selectedProviderId || ''}
                      onChange={(e) => handleProviderSelect(e.target.value)}
                    >
                      {providerGroups.map((g) => (
                        <option key={g.providerId} value={g.providerId}>
                          {t('analytics.provider.providerOption', { label: g.label, count: g.bots.length })}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group filter-group--wide">
                    <label className="filter-label" htmlFor="analytics-provider-bot-select">
                      {t('analytics.provider.bot')}
                    </label>
                    <select
                      id="analytics-provider-bot-select"
                      className="filter-select"
                      value={selectedProviderBotId || ''}
                      onChange={(e) => handleProviderBotSelect(e.target.value)}
                    >
                      {(selectedGroup?.bots || []).map((b) => (
                        <option key={b.botId} value={b.botId}>
                          {`${b.displayName || b.botId} — ${t(`status.${b.status}`, b.status)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {source === 'curated' && bots.length > 1 && (
                <div className="filter-group filter-group--wide">
                  <BotSelector
                    bots={bots}
                    selectedBotId={selectedBotId}
                    onBotSelect={handleBotSelect}
                  />
                </div>
              )}
              <div className="filter-group">
                <label className="filter-label">{t('analytics.filters.from')}</label>
                <input
                  type="date"
                  className="filter-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">{t('analytics.filters.to')}</label>
                <input
                  type="date"
                  className="filter-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <button
                className="filter-apply-button"
                onClick={handleApply}
                disabled={loading || !activeBotId}
              >
                {t('analytics.apply')}
              </button>
            </div>

            {source === 'provider' && selectedProviderBot && (
              <p className="analytics-scope-note">
                {t('analytics.provider.scopeNote', {
                  bot: selectedProviderBot.displayName || selectedProviderBot.botId,
                })}
                <span className="analytics-scope-id">{selectedProviderBot.botId}</span>
              </p>
            )}

            {/* Loading */}
            {loading && (
              <div className="analytics-loading">
                <p>{t('analytics.loading')}</p>
              </div>
            )}

            {/* 503 – Analytics DB not configured */}
            {!loading && unavailable && (
              <div className="analytics-unavailable">
                <p>{t('analytics.unavailable')}</p>
              </div>
            )}

            {/* 403 – Access denied for selected bot */}
            {!loading && forbidden && (
              <div className="analytics-error">
                <p>{t('analytics.accessDenied')}</p>
              </div>
            )}

            {/* Generic error */}
            {!loading && error && (
              <div className="analytics-error">
                <p>{error}</p>
              </div>
            )}

            {/* Analytics chat panel — shown when the feature flag is enabled,
                regardless of whether analytics data has loaded yet */}
            {analyticsChat.enabled && analyticsChat.origin && analyticsChat.integrationId && (
              <div className="analytics-section analytics-chat-section">
                <h3 className="section-title">{t('analytics.chat.title')}</h3>
                <p className="section-hint">{t('analytics.chat.hint')}</p>
                <p className="analytics-chat-example">{t('analytics.chat.example')}</p>
                <AnalyticsChat
                  origin={analyticsChat.origin}
                  integrationId={analyticsChat.integrationId}
                />
              </div>
            )}

            {/* Data panels */}
            {!loading && !unavailable && !forbidden && !error && applied && (
              <>
                {/* KPI Overview — provider bots show the owner's own tiles */}
                {appliedSource === 'provider' ? (
                  <div className="kpi-grid">
                    <KpiCard label={t('analytics.overview.totalMessages')} value={providerOverview?.total_messages} />
                    <KpiCard label={t('analytics.overview.activeUsers')} value={providerOverview?.active_users} />
                    <KpiCard label={t('analytics.overview.userMessages')} value={providerOverview?.user_messages} />
                    <KpiCard label={t('analytics.overview.assistantMessages')} value={providerOverview?.assistant_messages} />
                    <KpiCard label={t('analytics.overview.providerMessages')} value={providerOverview?.provider_messages} />
                  </div>
                ) : (
                  <div className="kpi-grid">
                    <KpiCard label={t('analytics.overview.totalMessages')} value={overview?.total_messages} />
                    <KpiCard label={t('analytics.overview.activeUsers')} value={overview?.active_users} />
                    <KpiCard label={t('analytics.overview.userMessages')} value={overview?.user_messages} />
                    <KpiCard label={t('analytics.overview.assistantMessages')} value={overview?.assistant_messages} />
                    <KpiCard label={t('analytics.overview.lineMessages')} value={overview?.line_messages} />
                    <KpiCard label={t('analytics.overview.webchatMessages')} value={overview?.webchat_messages} />
                    <KpiCard label={t('analytics.overview.pushedMessages')} value={overview?.pushed_messages} />
                  </div>
                )}
                {/* Daily messages chart */}
                <div className="analytics-section">
                  <h3 className="section-title">{t('analytics.dailyChart.title')}</h3>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={daily} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickLine={false}
                          axisLine={{ stroke: '#e5e7eb' }}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            fontSize: 13,
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Area
                          type="monotone"
                          dataKey="total"
                          name={t('analytics.dailyChart.total')}
                          stroke="#3b82f6"
                          fill="url(#colorTotal)"
                          strokeWidth={2}
                        />
                        <Area
                          type="monotone"
                          dataKey="active_users"
                          name={t('analytics.dailyChart.activeUsers')}
                          stroke="#10b981"
                          fill="url(#colorUsers)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Top users table */}
                <div className="analytics-section">
                  <h3 className="section-title">{t('analytics.topUsers.title')}</h3>
                  <p className="section-hint">{t('analytics.topUsers.clickHint')}</p>
                  <div className="analytics-table-wrapper">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>{t('analytics.topUsers.userId')}</th>
                          <th>{t('analytics.topUsers.totalMessages')}</th>
                          <th>{t('analytics.topUsers.userMessages')}</th>
                          <th>{t('analytics.topUsers.firstMessage')}</th>
                          <th>{t('analytics.topUsers.lastMessage')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topUsers.length === 0 ? (
                          <tr className="no-data-row">
                            <td colSpan={5}>{t('analytics.topUsers.noData')}</td>
                          </tr>
                        ) : (
                          topUsers.map((u) => (
                            <tr
                              key={u.user_id}
                              className="top-user-row"
                              onClick={() => handleUserRowClick(u.user_id)}
                              title={t('analytics.topUsers.viewHistory')}
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleUserRowClick(u.user_id);
                                }
                              }}
                            >
                              <td className="user-id-cell" title={u.user_id}>
                                {u.user_id}
                              </td>
                              <td>{formatNumber(u.total_messages)}</td>
                              <td>{formatNumber(u.user_messages)}</td>
                              <td>{formatDateTime(u.first_message_at)}</td>
                              <td>{formatDateTime(u.last_message_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Broadcasts table — LINE plane only */}
                {appliedSource === 'curated' && (
                <div className="analytics-section">
                  <h3 className="section-title">{t('analytics.broadcasts.title')}</h3>
                  <div className="analytics-table-wrapper">
                    <table className="analytics-table">
                      <thead>
                        <tr>
                          <th>{t('analytics.broadcasts.broadcastId')}</th>
                          <th>{t('analytics.broadcasts.sentAt')}</th>
                          <th>{t('analytics.broadcasts.bot')}</th>
                          <th>{t('analytics.broadcasts.topic')}</th>
                          <th>{t('analytics.broadcasts.status')}</th>
                          <th>{t('analytics.broadcasts.triggeredBy')}</th>
                          <th>{t('analytics.broadcasts.fanout')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {broadcasts.length === 0 ? (
                          <tr className="no-data-row">
                            <td colSpan={7}>{t('analytics.broadcasts.noData')}</td>
                          </tr>
                        ) : (
                          broadcasts.map((b) => (
                            <tr key={b.broadcast_id}>
                              <td className="broadcast-id-cell" title={b.broadcast_id}>
                                {b.broadcast_id}
                              </td>
                              <td>{formatDateTime(b.sent_at)}</td>
                              <td>{b.bot_id}</td>
                              <td className="topic-preview-cell" title={b.topic_preview}>
                                {b.topic_preview}
                              </td>
                              <td>{b.status}</td>
                              <td>{b.triggered_by}</td>
                              <td>{formatNumber(b.fanout_user_count)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* User messages drill-down panel */}
      {selectedUserId && (
        <UserMessagesPanel
          userId={selectedUserId}
          messages={userMessages}
          loading={userMessagesLoading}
          error={userMessagesError}
          onClose={handleCloseUserMessages}
        />
      )}
    </AdminLayout>
  );
};

export default AnalyticsPage;
